import { Action, AnyAction } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import {
  InstallerStatus,
  installerStatusResponses$,
} from '@pos-refacto/installer-rxjs';
import { take } from 'rxjs/operators';

import { getClientCode, getSessionKey } from 'reducers/Login';
import { Product } from 'types/Product';
import { getCafaEntry, getGeneralPrintingSettings } from 'reducers/cafaConfigs';
import { INTEGRATIONS, INTEGRATION_TYPES } from 'constants/CAFA';
import { getTemplateForType } from 'reducers/patchScript';
import { composeReceiptWithSchema } from 'reducers/patchScript/composeReceiptWithSchema';
import { getConnectionHealth } from 'reducers/connectivity/connection';
import * as api from 'services/ErplyAPI/sales';
import { getIsModuleEnabled } from 'reducers/configs/settings';
import { RootState } from 'reducers/index';
import { addError } from 'actions/Error';
import { getLastSaleActualReportsDataset } from 'reducers/sales';
import { STATISTIC_ENTRY_TYPES, Statistic } from 'utils/statistics/Statistics';
import { printingStatistics } from 'utils/statistics/PrintingStatistics';

import { getPatchScript as getPatchScriptFromReceiptPrintoutApi } from './receiptPrintoutApi/api';
import { GetPatchScriptParams } from './receiptPrintoutApi/types';
import { OfflineSalesDocument, Options, Row, SalesDocument } from './types';
import { renderPatchscript, wrapPngData } from './actions';
import { prepareReceiptData } from './preparePrintoutData';
import prepareOfflineData from './prepareOfflineData';

export const getSaleDocID = (salesDocument: {
  id?: string | number;
  invoiceID?: string | number;
}) => salesDocument.id ?? salesDocument.invoiceID;

export const mapRow = (
  product: Partial<Row & Product>,
  currencySymbol: string,
) => ({
  productID: String(product.productID),
  amount: String(product?.amount),
  amountAvailable: product?.free,
  amountInStock: product?.totalInStock,
  amountReserved: 0,
  brand: product?.brandName,
  category: product?.categoryName,
  code: product?.code,
  code2: product?.code2,
  code3: product?.code3,
  description: product?.description,
  discount: String(product?.discount),
  finalNetPrice: Number(product?.finalPrice),
  finalNetPriceWithCurrency: `${product.finalPrice} ${currencySymbol}`,
  finalPriceWithVAT: Number(product?.finalPriceWithVAT),
  finalPriceWithVATWithCurrency: `${product.finalPriceWithVAT} ${currencySymbol}`,
  grossWeight: String(product?.grossWeight),
  grossWeightWithUnits: `${product?.grossWeight} ${product?.unitName}`,
  imageURL1: product?.images?.[0],
  locationInWarehouse: product?.locationInWarehouse,
  longDescription: product?.longdesc,
  manufacturer: product?.manufacturerName,
  netWeight: String(product?.netWeight),
  netWeightWithUnits: `${product?.netWeight} ${product?.unitName}`,
  no: Number(product?.unitID),
  originalNetPrice: String(product?.basePriceNet),
  originalNetPriceWithCurrency: `${product.basePriceNet} ${currencySymbol}`,
  originalPriceWithVAT: Number(product?.basePriceWithVAT),
  originalPriceWithVATWithCurrency: `${product.basePriceWithVAT} ${currencySymbol}`,
  packages: product?.packages,
  productGrossWeight: String(product?.grossWeight),
  productGrossWeightWithUnit: `${product?.grossWeight} ${product?.unitName}`,
  productGroup: product?.groupName,
  productHeight: String(product?.height),
  productHeightWithUnit: `${product?.height} ${product?.unitName}`,
  productLength: String(product?.length),
  productLengthWithUnit: `${product?.length} ${product?.unitName}`,
  productName: product?.name,
  productNetWeight: String(product?.netWeight),
  productNetWeightWithUnit: `${product?.netWeight} ${product?.unitName}`,
  productVolume: String(product?.volume),
  productVolumeWithUnit: `${product?.volume} ${product?.unitName}`,
  productWidth: String(product?.width),
  productWidthWithUnit: `${product?.width} ${product?.unitName}`,
  realPercentageDiscount: String(product?.discount),
  rowNetTotal: Number(product?.rowNetTotal),
  rowNetTotalWithCurrency: `${product.rowNetTotal} ${currencySymbol}`,
  rowTotal: Number(product?.rowTotal),
  rowTotalWithCurrency: `${product.rowTotal} ${currencySymbol}`,
  rowVAT: Number(product?.rowVATTotal),
  rowVATWithCurrency: `${product.rowVATTotal} ${currencySymbol}`,
  supplier: product?.supplierName,
  supplierCode: String(product?.supplierCode),
  title: product?.name,
  unit: product?.unitName,
  usualDeliveryTime: String(product?.deliveryTime),
  vatRate: `${product?.vatRate} %`,
});

export const mapRowForActualDataSet = (
  product: Partial<Row & Product>,
  currencySymbol: string,
) => ({
  productID: String(product.productID),
  amount: String(product?.amount),
  amountAvailable: product?.free,
  amountInStock: product?.totalInStock,
  amountReserved: 0,
  brand: product?.brandName,
  category: product?.categoryName,
  code: product?.code,
  code2: product?.code2,
  code3: product?.code3,
  description: product?.description,
  discount: String(product?.discount),
  finalNetPrice: String(product?.finalPrice),
  finalNetPriceWithCurrency: `${product.finalPrice} ${currencySymbol}`,
  finalPriceWithVAT: String(product?.finalPriceWithVAT),
  finalPriceWithVATWithCurrency: `${product.finalPriceWithVAT} ${currencySymbol}`,
  grossWeight: String(product?.grossWeight),
  grossWeightWithUnits: `${product?.grossWeight} ${product?.unitName}`,
  imageURL1: product?.images?.[0],
  locationInWarehouse: product?.locationInWarehouse,
  longDescription: product?.longdesc,
  manufacturer: product?.manufacturerName,
  netWeight: String(product?.netWeight),
  netWeightWithUnits: `${product?.netWeight} ${product?.unitName}`,
  no: Number(product?.unitID),
  originalNetPrice: String(product?.basePriceNet),
  originalNetPriceWithCurrency: `${product.basePriceNet} ${currencySymbol}`,
  originalPriceWithVAT: Number(product?.basePriceWithVAT),
  originalPriceWithVATWithCurrency: `${product.basePriceWithVAT} ${currencySymbol}`,
  packages: product?.packages,
  productGrossWeight: String(product?.grossWeight),
  productGrossWeightWithUnit: `${product?.grossWeight} ${product?.unitName}`,
  productGroup: product?.groupName,
  productHeight: String(product?.height),
  productHeightWithUnit: `${product?.height} ${product?.unitName}`,
  productLength: String(product?.length),
  productLengthWithUnit: `${product?.length} ${product?.unitName}`,
  productName: product?.name,
  productNetWeight: String(product?.netWeight),
  productNetWeightWithUnit: `${product?.netWeight} ${product?.unitName}`,
  productVolume: String(product?.volume),
  productVolumeWithUnit: `${product?.volume} ${product?.unitName}`,
  productWidth: String(product?.width),
  productWidthWithUnit: `${product?.width} ${product?.unitName}`,
  realPercentageDiscount: String(product?.discount),
  rowNetTotal: String(product?.rowNetTotal),
  rowNetTotalWithCurrency: `${product.rowNetTotal} ${currencySymbol}`,
  rowTotal: String(product?.rowTotal),
  rowTotalWithCurrency: `${product.rowTotal} ${currencySymbol}`,
  rowVAT: String(product?.rowVATTotal),
  rowVATWithCurrency: `${product.rowVATTotal} ${currencySymbol}`,
  supplier: product?.supplierName,
  supplierCode: String(product?.supplierCode),
  title: product?.name,
  unit: product?.unitName,
  usualDeliveryTime: String(product?.deliveryTime),
  vatRate: `${product?.vatRate} %`,
});

export const getIntegrationType = (options: Options) => {
  if (options?.giftReceipt) {
    return 'giftReceipt';
  }
  if (options?.order) {
    return 'order';
  }
  if (options?.offer) {
    return 'quote';
  }
  if (options?.kitchen) {
    return 'kitchen';
  }
  if (options?.bar) {
    return 'bar';
  }
  return 'salesReceipt';
};

type Integration = InstallerStatus['data']['integrations'][0];

export const getMsInfo = (
  integrationName: string,
): ThunkAction<
  Promise<undefined | Integration>,
  RootState,
  unknown,
  AnyAction
> => async dispatch => {
  // MSs cannot be ran on Wrapper devices, so don't even ping installer
  if (window.wrapperInfo) {
    return undefined;
  }
  try {
    const res = await installerStatusResponses$
      .pipe<InstallerStatus | Error>(take(1))
      .toPromise();
    if (res instanceof Error) {
      // Installer not running (f.ex. wrappers, but also unintegrated desktops)
      return undefined;
    }
    return res?.data.integrations.find(int => int.name === integrationName);
  } catch (error) {
    dispatch(
      addError(error instanceof Error ? error.message : 'Can not get MS info'),
    );
    return undefined;
  }
};

export const getIsMSRunning = (
  integrationName: string,
): ThunkAction<
  Promise<boolean>,
  RootState,
  unknown,
  AnyAction
> => async dispatch => {
  const res = await dispatch(getMsInfo(integrationName));
  if (!res) {
    // Installer not running (f.ex. wrappers, but also unintegrated desktops)
    return false;
  }
  return res.status === 'StatusRunning';
};

const getPatchScript = (
  params: GetPatchScriptParams,
  options: Options,
) => async (
  dispatch: ThunkDispatch<RootState, unknown, Action>,
  getState: () => RootState,
) => {
  const { id: templateId } =
    getCafaEntry(
      getIntegrationType(options),
      INTEGRATION_TYPES.patchScriptTemplate,
    )(getState()) ?? {};

  const receiptPrintoutApiIsRunning = await dispatch(
    getIsMSRunning('receipt-printout-api'),
  );
  if (!receiptPrintoutApiIsRunning) {
    throw new Error('Receipt printout API MS not running');
  }

  if (!templateId) {
    throw new Error('Unable to find patchscript template id');
  }

  const patchscript = await getPatchScriptFromReceiptPrintoutApi(
    getSessionKey(getState()),
    getClientCode(getState()),
    { ...params, templateId: String(templateId) },
  );

  return patchscript;
};

export const getLocalPatchScript = (
  salesDocument: SalesDocument,
  options: Options,
) => async (
  dispatch: ThunkDispatch<RootState, unknown, Action>,
  getState: () => RootState,
) => {
  const integrationType = getIntegrationType(options);

  const data = await dispatch(
    prepareReceiptData({ salesDocument, type: integrationType }),
  );

  const patchScriptTemplate = getTemplateForType(
    INTEGRATIONS[INTEGRATION_TYPES.patchScriptTemplate][integrationType],
  )(getState());

  const {
    forcePatchscriptRender: { enabled, pixelWidth, scaleMultiplier },
  } = getGeneralPrintingSettings(getState());

  const patchScript = await (enabled
    ? data =>
        renderPatchscript(data, pixelWidth, scaleMultiplier).then(wrapPngData)
    : data => Promise.resolve(data))(
    composeReceiptWithSchema(patchScriptTemplate, data),
  );

  return patchScript;
};

export const getFinalPatchScript = (
  salesDocument: SalesDocument,
  options: Options,
) => async (dispatch: ThunkDispatch<RootState, unknown, Action>) => {
  const docId = Number(getSaleDocID(salesDocument));
  try {
    printingStatistics.init({
      itemType: STATISTIC_ENTRY_TYPES.TypeNameDownload,
      description: `Getting PatchScript from receipt-printout-api (docId: ${docId})`,
    });
    const patchScript = await dispatch(
      getPatchScript(
        {
          salesDocumentId: docId,
          printoutType: 'PATCH_SCRIPT',
        },
        options,
      ),
    );
    printingStatistics.finishLog(
      `Getting PatchScript from receipt-printout-api (docId: ${docId})`,
    );
    return patchScript;
  } catch (error) {
    const msg: string = (() => {
      if ('message' in error) {
        return error.message;
      }
      if (typeof error === 'string') {
        return error;
      }
      return '';
    })();
    printingStatistics.finishLog(
      `Getting PatchScript from receipt-printout-api (docId: ${docId})`,
      msg,
    );
    printingStatistics.init({
      itemType: STATISTIC_ENTRY_TYPES.TypeNameDataProcessing,
      description: `Creating PatchScript from POS (docId: ${docId})`,
    });
    const patchScript = await dispatch(
      getLocalPatchScript(salesDocument, options),
    );
    printingStatistics.finishLog(
      `Creating PatchScript from POS (docId: ${docId})`,
    );
    return patchScript;
  }
};

export const getOfflinePatchScript = (
  salesDocument: OfflineSalesDocument,
  options: Options = {},
) => async (dispatch: ThunkDispatch<RootState, unknown, Action>) => {
  try {
    const { documentActualReport, salesDocument: doc } = await dispatch(
      prepareOfflineData({ salesDocument }),
    );
    const patchScript = await dispatch(
      getPatchScript(
        {
          documentActualReport,
          salesDocument: doc,
          printoutType: 'PATCH_SCRIPT',
        },
        options,
      ),
    );
    return patchScript;
  } catch (error) {
    const patchScript = await dispatch(
      getLocalPatchScript(salesDocument, options),
    );
    return patchScript;
  }
};

export const getSalesDocumentActualReportsDataset = (
  saleDocumentId: number,
) => async (
  dispatch: ThunkDispatch<RootState, unknown, Action>,
  getState: () => RootState,
) => {
  const cachedDataset = getLastSaleActualReportsDataset(getState());
  if (cachedDataset?.documentID === saleDocumentId) {
    return cachedDataset;
  }
  return api
    .getSalesDocumentActualReportsDataset({
      dataContentType: 'PLAIN_TEXT',
      salesDocumentID: saleDocumentId,
    })
    .then(records => records[0]);
};

export const getDocumentARDataset = (salesDocument: SalesDocument) => async (
  dispatch: ThunkDispatch<RootState, unknown, Action>,
  getState: () => RootState,
) => {
  const isOnline = getConnectionHealth(getState());

  const getSaleDocumentId = async () => {
    // means that doc was made offline, thats why theres no invoiceLink
    if (!salesDocument.invoiceLink) {
      const [doc] = await api.getSalesDocuments({
        number: (salesDocument as OfflineSalesDocument).number,
      });
      return doc.id;
    }
    return getSaleDocID(salesDocument);
  };

  if (isOnline) {
    const saleDocumentId = await getSaleDocumentId();
    const doc = await dispatch(
      getSalesDocumentActualReportsDataset(Number(saleDocumentId)),
    );
    return doc;
  }
  const { documentActualReport } = await dispatch(
    prepareOfflineData({
      salesDocument: salesDocument as OfflineSalesDocument,
    }),
  );
  return documentActualReport;
};

export const existingDialects = {
  en: ['AU', 'CA', 'US'],
};

export function withPrintingStatisticsAction(
  options: Pick<Statistic, 'itemType' | 'description'>,
) {
  return <Params extends any[], R extends any>(
    action: (
      ...params: Params
    ) => ThunkAction<Promise<R>, RootState, unknown, Action>,
  ) => (...params: Params) => async (
    dispatch: ThunkDispatch<RootState, unknown, Action>,
  ) => {
    printingStatistics.init(options);
    try {
      const result = await dispatch(action(...params));
      printingStatistics.finishLog(options.description);
      return result;
    } catch (error) {
      printingStatistics.finishLog(options.description, error?.message);
      throw error;
    }
  };
}

export function withPrintingStatisticsFunction(
  options: Pick<Statistic, 'itemType' | 'description'>,
) {
  return <Params extends any[], R extends any>(
    func: (...params: Params) => Promise<R>,
  ) => async (...params: Params) => {
    printingStatistics.init(options);
    try {
      const result = await func(...params);
      printingStatistics.finishLog(options.description);
      return result;
    } catch (error) {
      printingStatistics.finishLog(options.description, error?.message);
      throw error;
    }
  };
}
